import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Badge } from "../components/Badge";
import { fmtBDT } from "../lib/format";
import {
  Plus, Trash2, Star, X, Edit3, Copy, Wifi, CheckCircle2, XCircle,
  AlertTriangle, Eye, EyeOff, Globe, Key, Link2, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const METHODS = ["bkash", "nagad", "rocket", "upay", "upi", "imps", "bank", "usdt_trc20", "usdt_bep20", "usdt_erc20", "trx", "bnb", "eth", "ton", "usdc_polygon"];
const PROVIDERS = [
  { id: "starpago", label: "StarPago (BD)", fields: ["app_id", "secret_key", "base_url", "notify_url_base", "country_code"] },
  { id: "hrpay", label: "HRpay (BD)", fields: ["merchant_id", "api_key", "api_url", "notify_url_base", "currency_id"] },
  { id: "cowpay", label: "Cowpay (BD)", fields: ["merchant_code", "secret_key", "base_url", "notify_url_base", "country_code"] },
  { id: "nowpayments", label: "NowPayments (Crypto)", fields: ["api_key", "ipn_secret", "api_base", "notify_url_base", "rate_bdt_per_usd", "coins"] },
  { id: "wpay", label: "WPay (wpay.one / okpay)", fields: ["mch_id", "secret_key", "base_url", "notify_url_base", "currency", "pay_type_payin", "payin_pay_types", "payout_channels", "return_url"] },
  { id: "lgpay", label: "LG-Pay", fields: ["merchant_id", "secret_key", "api_url", "notify_url_base"] },
  { id: "china", label: "China Gateway", fields: ["api_url", "api_key", "secret"] },
  { id: "mock", label: "Mock (testing)", fields: ["webhook_secret"] },
];

const CONFIG_FIELD_META = {
  app_id:         { label: "Application ID",   help: "StarPago: appId from merchant panel (https://bd.merchant.starpago.com)" },
  merchant_code:  { label: "Merchant Code",    help: "Supplied by the provider after onboarding" },
  merchant_id:    { label: "Merchant ID",      help: "Your unique merchant identifier (HRpay: ShopUserLongId)" },
  mch_id:         { label: "Merchant ID (mchId)", help: "WPay: numeric merchant id provided in your wpay.one merchant panel (sandbox uses 1000)" },
  secret_key:     { label: "Secret Key",       secret: true, help: "Used for signing requests (MD5/HMAC/SHA-256)" },
  api_key:        { label: "API Key",          secret: true, help: "HRpay: API Connection Key / HashKey used for SHA-256 signing" },
  api_url:        { label: "API Base URL",     help: "Provider's API endpoint, e.g. https://mdf.hr5688.com", mono: true },
  base_url:       { label: "API Base URL",     help: "WPay: https://api.wpay.one (prod) or https://sandbox.wpay.one (test)", mono: true },
  secret:         { label: "Secret",           secret: true },
  webhook_secret: { label: "Webhook Secret",   secret: true, help: "Shared secret for verifying callbacks" },
  notify_url_base:{ label: "Notify URL base",  help: "Your public domain — callbacks from provider will hit this domain. Blank = use PUBLIC_URL.", mono: true },
  country_code:   { label: "Country Code",     help: "BD for Bangladesh" },
  currency_id:    { label: "Currency ID",      help: "HRpay currency — 11 for BDT (Bangladesh)" },
  currency:       { label: "Currency",         help: "ISO code: INR, BDT, etc." },
  pay_type_payin: { label: "Default Pay Type (pay-in)", help: "WPay channel for /v1/Collect (e.g. BKASH, NAGAD, UPI). Used when no per-method override is set." },
  payin_pay_types:{ label: "Per-method Pay Type map (JSON)", mono: true, help: 'JSON map of method-id → WPay pay_type code for /v1/Collect, e.g. {"bkash":"BKASH","nagad":"NAGAD"}' },
  payout_channels:{ label: "Payout pay_type map (JSON)", mono: true, help: 'JSON map of method-id → WPay pay_type code for /v1/Payout, e.g. {"bkash":"BKASH","nagad":"NAGAD"}' },
  return_url:     { label: "Return URL",       help: "Browser redirect after pay-in (optional)", mono: true },
  ipn_secret:     { label: "IPN Secret",        secret: true, help: "NowPayments IPN secret key (HMAC-SHA512 sign verification)" },
  api_base:       { label: "API Base URL",       help: "NowPayments API base, e.g. https://api.nowpayments.io/v1", mono: true },
  rate_bdt_per_usd:{ label: "Default rate (1 USD = ? BDT)", help: "Used when a coin doesn't specify its own override" },
  coins:          { label: "Per-coin config (JSON)", mono: true, help: 'JSON map keyed by coin id, e.g. {"usdt_trc20":{"enabled":true,"min_usd":2}}' },
  mch_no:          { label: "Merchant No (mchNo)", help: "TarsPay: merchant number from your TarsPay dashboard (e.g. M1688443982)" },
  private_key:     { label: "Private Key (ECDSA)", secret: true, mono: true, help: "TarsPay: hex-encoded secp256k1 private key from Account → APIKey → New" },
  public_key:      { label: "Public Key (ECDSA)", mono: true, help: "TarsPay: hex-encoded compressed secp256k1 public key (goes in X-API-KEY header)" },
  system_public_key:{ label: "System Public Key", mono: true, help: "TarsPay platform verify key — used to verify callback signatures. Leave blank to use the default published in TarsPay docs." },
  way_codes:       { label: "Method → wayCode map (JSON)", mono: true, help: 'JSON map of internal method id → TarsPay wayCode, e.g. {"bkash":"EWALLET_BKASH","nagad":"EWALLET_NAGAD","bkash_official":"EWALLET_BKASH_OFFICIAL"}' },
};

const emptyForProvider = (provider = "cowpay") => ({
  provider,
  label: "",
  methods: ["bkash", "nagad"],
  enabled: true,
  recommended: false,
  priority: 100,
  min_amount: 100,
  max_amount: 100000,
  config: {},
});

export default function Gateways() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [revealSecrets, setRevealSecrets] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/site/admin/gateways");
      setRows(data.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const open = (row) => {
    if (row) {
      setForm({ ...row, config: { ...(row.config || {}) } });
      setEditingId(row.id);
    } else {
      setForm(emptyForProvider("cowpay"));
      setEditingId(null);
    }
  };

  const toggleMethod = (m) => {
    const on = form.methods.includes(m);
    setForm({ ...form, methods: on ? form.methods.filter((x) => x !== m) : [...form.methods, m] });
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      provider: form.provider,
      label: form.label,
      methods: form.methods,
      enabled: !!form.enabled,
      recommended: !!form.recommended,
      priority: parseInt(form.priority) || 0,
      min_amount: form.min_amount === "" ? null : parseFloat(form.min_amount),
      max_amount: form.max_amount === "" ? null : parseFloat(form.max_amount),
      config: form.config || {},
    };
    try {
      if (editingId) {
        await api.put(`/site/admin/gateways/${editingId}`, payload);
        toast.success("Gateway saved — snapshot written to payment_gateways_config.json");
      } else {
        await api.post("/site/admin/gateways", payload);
        toast.success("Gateway created");
      }
      setForm(null);
      setEditingId(null);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to save gateway";
      toast.error(typeof msg === "string" ? msg : "Error");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete gateway "${row.label}"? This cannot be undone.`)) return;
    await api.delete(`/site/admin/gateways/${row.id}`);
    toast.success("Gateway deleted");
    load();
  };

  const copyText = (text, label = "Copied") => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const currentProvider = PROVIDERS.find((p) => p.id === form?.provider) || PROVIDERS[0];

  return (
    <div data-testid="gateways-page">
      <PageHeader
        title="Payment Gateways"
        subtitle="All changes auto-save to payment_gateways_config.json · survives VPS redeploys"
        actions={(
          <button
            onClick={() => open(null)}
            className="h-9 px-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-1.5"
            data-testid="gateway-add-btn"
          >
            <Plus size={14} /> Add Gateway
          </button>
        )}
      />

      {/* ============ Form ============ */}
      {form && (
        <form onSubmit={submit} className="bg-white border border-gray-200 rounded-sm p-5 mb-6 space-y-4 fade-in-up" data-testid="gateway-form">
          <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
            <div className="font-display text-lg font-bold">{editingId ? "Edit Gateway" : "New Gateway"}</div>
            {editingId && (
              <Badge variant="info" className="ml-2">
                <span className="text-[10px] font-mono">{editingId.slice(0, 8)}</span>
              </Badge>
            )}
            <button
              type="button"
              onClick={() => { setForm(null); setEditingId(null); }}
              className="ml-auto h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:bg-gray-50"
            >
              <X size={14} />
            </button>
          </div>

          {/* Basic fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Provider">
              <select
                value={form.provider}
                onChange={(e) => setForm({ ...form, provider: e.target.value })}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm bg-white"
                data-testid="gw-provider"
                disabled={!!editingId}
              >
                {PROVIDERS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </Field>
            <Field label="Label (shown to users)">
              <input required value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. Cowpay (bKash / Nagad)"
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="gw-label" />
            </Field>
            <Field label="Priority" help="Higher shown first">
              <input type="number" value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" />
            </Field>
            <Field label="Min Amount (৳)">
              <input type="number" step="0.01" value={form.min_amount ?? ""}
                onChange={(e) => setForm({ ...form, min_amount: e.target.value })}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" />
            </Field>
            <Field label="Max Amount (৳)">
              <input type="number" step="0.01" value={form.max_amount ?? ""}
                onChange={(e) => setForm({ ...form, max_amount: e.target.value })}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" />
            </Field>
            <Field label="Methods enabled">
              <div className="flex flex-wrap gap-1.5">
                {METHODS.map((m) => (
                  <button key={m} type="button" onClick={() => toggleMethod(m)}
                    className={`px-3 h-10 border rounded-sm text-xs font-semibold uppercase tracking-wider transition-colors ${
                      form.methods.includes(m)
                        ? "bg-emerald-500 text-white border-emerald-500"
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                    }`}
                    data-testid={`gw-method-${m}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </Field>
          </div>

          {/* Status switches */}
          <div className="flex items-center gap-4 pt-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                data-testid="gw-enabled" className="h-4 w-4 accent-emerald-500" />
              <span>Enabled</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!form.recommended}
                onChange={(e) => setForm({ ...form, recommended: e.target.checked })}
                data-testid="gw-recommended" className="h-4 w-4 accent-amber-500" />
              <span className="inline-flex items-center gap-1">Recommended <Star size={12} className="text-amber-500 fill-amber-500" /></span>
            </label>
          </div>

          {/* Provider-specific config fields */}
          <div className="border border-gray-200 rounded-sm p-4 bg-gray-50/50 space-y-3">
            <div className="flex items-center gap-2">
              <Key size={14} className="text-gray-400" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-700">
                {currentProvider.label} credentials
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {currentProvider.fields.map((fieldKey) => {
                const meta = CONFIG_FIELD_META[fieldKey] || { label: fieldKey };
                const reveal = revealSecrets[fieldKey];
                const isSecret = meta.secret;
                const rawVal = form.config?.[fieldKey];
                const displayVal =
                  rawVal === undefined || rawVal === null
                    ? ""
                    : typeof rawVal === "object"
                    ? JSON.stringify(rawVal)
                    : rawVal;
                return (
                  <Field key={fieldKey} label={meta.label} help={meta.help}>
                    <div className="relative">
                      <input
                        type={isSecret && !reveal ? "password" : "text"}
                        value={displayVal}
                        onChange={(e) => setForm({ ...form, config: { ...form.config, [fieldKey]: e.target.value } })}
                        placeholder={isSecret ? "Leave blank to keep existing" : ""}
                        className={`w-full h-10 px-3 ${isSecret ? "pr-10" : ""} border border-gray-200 rounded-sm text-sm ${meta.mono ? "font-mono" : ""}`}
                        data-testid={`gw-config-${fieldKey}`}
                      />
                      {isSecret && (
                        <button
                          type="button"
                          onClick={() => setRevealSecrets((s) => ({ ...s, [fieldKey]: !reveal }))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center text-gray-400 hover:text-gray-700"
                          tabIndex={-1}
                        >
                          {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      )}
                    </div>
                  </Field>
                );
              })}
            </div>
          </div>

          {/* Callback URLs + Test connection (only for existing gateway) */}
          {editingId && <CallbackAndTestPanel gatewayId={editingId} />}

          {/* Submit row */}
          <div className="flex justify-end pt-3 border-t border-gray-100 gap-2">
            <button type="button" onClick={() => { setForm(null); setEditingId(null); }}
              className="h-9 px-4 border border-gray-200 rounded-sm text-sm">
              Cancel
            </button>
            <button type="submit"
              className="h-9 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold"
              data-testid="gw-submit">
              {editingId ? "Save & Sync" : "Create Gateway"}
            </button>
          </div>
        </form>
      )}

      {/* ============ Gateway list ============ */}
      {loading ? (
        <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">
          No payment gateways configured. Click "Add Gateway" to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="gateways-grid">
          {rows.map((r) => (
            <GatewayCard
              key={r.id}
              row={r}
              onEdit={() => open(r)}
              onDelete={() => remove(r)}
              onCopy={copyText}
            />
          ))}
        </div>
      )}

      {/* VPS deploy hint bar */}
      <div className="mt-6 bg-slate-900 text-slate-100 rounded-sm p-4 text-xs flex items-start gap-3">
        <Globe size={16} className="text-emerald-400 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="font-bold text-slate-50 mb-1">VPS deployment</div>
          <div className="text-slate-300 leading-relaxed">
            Every save writes to <code className="text-emerald-300 bg-slate-800 px-1 rounded">/app/backend/payment_gateways_config.json</code>.
            Copy this single file to your VPS and restart — all gateways + credentials are restored automatically.
            See <code className="text-emerald-300 bg-slate-800 px-1 rounded">VPS_DEPLOY.md</code> for the full guide.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Gateway card ---------- */
function GatewayCard({ row: r, onEdit, onDelete, onCopy }) {
  const credsOk = configHasCreds(r);
  return (
    <div className={`bg-white border ${r.recommended ? "border-emerald-300" : "border-gray-200"} rounded-sm p-5`} data-testid={`gw-card-${r.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="font-display text-lg font-bold text-gray-900">{r.label}</div>
            {r.recommended && <Star size={14} className="text-amber-500 fill-amber-500" />}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-0.5">
            <span className="font-mono text-gray-700">{r.provider}</span>
            <span className="mx-1.5 text-gray-300">·</span>
            Priority {r.priority}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!credsOk && (
            <Badge variant="warn" className="inline-flex items-center gap-0.5">
              <AlertTriangle size={10} strokeWidth={2.5} /> Missing creds
            </Badge>
          )}
          <Badge variant={r.enabled ? "success" : "default"}>{r.enabled ? "enabled" : "disabled"}</Badge>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mb-3">
        {(r.methods || []).map((m) => (
          <span key={m} className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-gray-50 text-gray-700 border border-gray-200">{m}</span>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs text-gray-600 mb-3">
        <div><span className="text-gray-500">Min:</span> <span className="font-semibold tabular">{r.min_amount ? fmtBDT(r.min_amount) : "—"}</span></div>
        <div><span className="text-gray-500">Max:</span> <span className="font-semibold tabular">{r.max_amount ? fmtBDT(r.max_amount) : "—"}</span></div>
      </div>

      {r.config && Object.keys(r.config).length > 0 && (
        <div className="text-[11px] text-gray-500 font-mono bg-gray-50 border border-gray-100 rounded-sm p-2 mb-3 space-y-0.5">
          {Object.entries(r.config).filter(([k]) => k !== "label" && k !== "_gateway_id").map(([k, v]) => {
            const meta = CONFIG_FIELD_META[k] || {};
            const displayV =
              v === null || v === undefined
                ? ""
                : typeof v === "object"
                ? JSON.stringify(v)
                : String(v);
            const shown = meta.secret && displayV ? `${displayV.slice(0, 4)}••••${displayV.slice(-4)}` : (displayV || "—");
            return (
              <div key={k} className="flex items-center gap-1 truncate">
                <span className="text-gray-400 shrink-0">{k}:</span>
                <span className="text-gray-700 truncate">{shown}</span>
                {displayV && (
                  <button onClick={() => onCopy(displayV, meta.label || k)} className="ml-auto text-gray-400 hover:text-gray-700 shrink-0" title="Copy">
                    <Copy size={11} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-1 pt-3 border-t border-gray-100">
        <button onClick={onEdit} className="h-8 px-3 inline-flex items-center gap-1.5 text-xs border border-gray-200 rounded-sm hover:bg-gray-50" data-testid={`gw-edit-${r.id}`}>
          <Edit3 size={12} /> Edit / Test
        </button>
        <button onClick={onDelete} className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-red-600 hover:border-red-200 ml-auto" data-testid={`gw-delete-${r.id}`}>
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

/* ---------- Callback URLs + Test Connection panel ---------- */
function CallbackAndTestPanel({ gatewayId }) {
  const [urls, setUrls] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/site/admin/gateways/${gatewayId}/callback-urls`);
        setUrls(data);
      } catch {
        setUrls(null);
      }
    })();
  }, [gatewayId]);

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/site/admin/gateways/${gatewayId}/test-connection`);
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, stage: "error", error: err?.response?.data?.detail || err?.message || "Unknown error" });
    } finally {
      setTesting(false);
    }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };
  const resultColor = testResult == null ? "gray"
    : testResult.ok === true ? "emerald"
    : testResult.ok === false ? "red"
    : "amber";
  const ResultIcon = testResult == null ? null : testResult.ok === true ? CheckCircle2 : testResult.ok === false ? XCircle : AlertTriangle;

  return (
    <div className="border border-gray-200 rounded-sm p-4 bg-blue-50/40 space-y-3">
      <div className="flex items-center gap-2">
        <Link2 size={14} className="text-gray-400" />
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-700">Callback URLs (register these in the provider's dashboard)</span>
      </div>
      {urls?.urls ? (
        <div className="space-y-2">
          {Object.entries(urls.urls).map(([key, url]) => (
            <div key={key} className="flex items-stretch gap-2">
              <div className="w-28 flex-shrink-0 flex items-center text-[10px] font-bold uppercase text-gray-600 bg-white border border-gray-200 px-2 rounded-sm">
                {key.replace(/_/g, " ")}
              </div>
              <div className="flex-1 flex items-center bg-white border border-gray-200 rounded-sm px-2 font-mono text-[11px] text-gray-800 truncate" title={url}>
                {url}
              </div>
              <button type="button" onClick={() => copy(url)} className="h-8 px-2 bg-white border border-gray-200 text-gray-600 rounded-sm text-xs font-semibold hover:bg-gray-50 inline-flex items-center gap-1">
                <Copy size={11} /> Copy
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-gray-500">Save the gateway first to see callback URLs.</div>
      )}

      <div className="pt-2 border-t border-gray-200 space-y-2">
        <div className="flex items-center gap-2">
          <Wifi size={14} className="text-gray-400" />
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-700">Diagnostic test</span>
          <button type="button" onClick={runTest} disabled={testing}
            className="ml-auto h-8 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="gw-test-connection">
            {testing ? <><Loader2 size={12} className="animate-spin" /> Testing…</> : <><Wifi size={12} /> Test connection</>}
          </button>
        </div>
        {testResult && (
          <div className={`rounded-sm border p-3 text-xs ${
            resultColor === "emerald" ? "bg-emerald-50 border-emerald-200 text-emerald-900"
            : resultColor === "red" ? "bg-red-50 border-red-200 text-red-900"
            : "bg-amber-50 border-amber-200 text-amber-900"
          }`} data-testid="gw-test-result">
            <div className="flex items-start gap-2">
              {ResultIcon && <ResultIcon size={16} className="mt-0.5 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-bold uppercase tracking-wider text-[10px]">{testResult.stage}</div>
                <div className="mt-0.5 leading-relaxed break-words">{testResult.message || testResult.error}</div>
                {testResult.raw && (
                  <details className="mt-1.5">
                    <summary className="cursor-pointer text-[10px] opacity-70">Raw response</summary>
                    <pre className="mt-1 text-[10px] font-mono whitespace-pre-wrap bg-white/50 p-2 rounded-sm">{JSON.stringify(testResult.raw, null, 2)}</pre>
                  </details>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Helpers ---------- */
function configHasCreds(r) {
  const cfg = r?.config || {};
  const p = r?.provider;
  if (p === "starpago") return !!(cfg.app_id && cfg.secret_key);
  if (p === "hrpay") return !!(cfg.merchant_id && cfg.api_key);
  if (p === "cowpay") return !!(cfg.merchant_code && cfg.secret_key);
  if (p === "wpay") return !!(cfg.mch_id && cfg.secret_key);
  if (p === "tarspay") return !!(cfg.mch_no && cfg.private_key && cfg.public_key);
  if (p === "nowpayments") return !!(cfg.api_key && cfg.ipn_secret);
  if (p === "lgpay") return !!(cfg.merchant_id && cfg.secret_key);
  if (p === "china") return !!(cfg.api_url && cfg.api_key);
  if (p === "mock") return !!cfg.webhook_secret;
  return true;
}

function Field({ label, children, help }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>
      {children}
      {help && <div className="text-[11px] text-gray-400 mt-1">{help}</div>}
    </div>
  );
}
